import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";

import {
  Container,
  CircularProgress,
  TextField,
  Grid2,
  Typography,
  Box,
  Divider,
  FormControlLabel,
  Switch,
  Tooltip,
  Button,
} from "@mui/material";
import { toast } from "react-toastify";

import moment from "moment";

import StateSelector from "app/Components/Fields/StateSelector";
import CitySelector from "app/Components/Fields/CitySelector";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";

import SaveIcon from "@mui/icons-material/Save";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import DeleteGameModal from "app/Components/Modals/DeleteGameModal";

import {
  createGame,
  editGame,
  deleteGame,
} from "_DatabaseFunctions/directConnect/hosts/gameFunctions";

import { createHash } from "utils/helperFunctions";

import { db } from "config/firebase";
import { doc, getDoc } from "firebase/firestore";
import AutocompleteAddress from "app/Components/Fields/AutocompleteAddress";
import { validateInput } from "./utils/ValidateInput";
import ZipField from "./components/ZipField";
import Description from "./components/Description";
import SelectField from "./components/SelectField";
import NumberField from "./components/NumberField";
import TextInput from "./components/TextInput";

const STAKE_VALUES = ["1-2", "1-3", "2-5", "5-5", "1-2-5"] as const;
const GAME_VALUES = ["NLH", "PLO", "Big-O", "Mixed"] as const;
const FEE_VALUES = ["0", "5", "10", "20"] as const;
const BUYIN_VALUES = ["0", "10", "20", "50", "100"] as const;

interface GameFormData {
  gameName: string;
  type: "Cash" | "Tournament";
  stakes: string;
  game: string;
  fee: string;
  state: string;
  city: string;
  zipcode: string;
  address: string;
  lat?: number;
  lng?: number;
  access: "Public" | "Private";
  accesscode: string;
  minPlayers: number;
  maxPlayers: number;
  minbuyin: string;
  maxbuyin: string;
  startMoment: moment.Moment;
  startTime: string;
  startDay: number;
  outputMsg: string;
  customstakes: string;
  customgame: string;
  customfee: string;
  custombuyin: string;
  buyin?: string;
  description?: string;
  id?: string;
  status?: string;
  visible: boolean;
}

interface Errors {
  [key: string]: string;
}

const OPTIONS = {
  Access: [
    { value: "Public", label: "Public" },
    { value: "Private", label: "Private" },
  ],
  Type: [
    { value: "Cash", label: "Cash" },
    { value: "Tournament", label: "Tournament" },
  ],
  Stakes: [
    { value: "1-2", label: "1-2" },
    { value: "1-3", label: "1-3" },
    { value: "2-5", label: "2-5" },
    { value: "5-5", label: "5-5" },
    { value: "1-2-5", label: "1-2-5" },
    { value: "Custom", label: "Custom" },
  ],
  BuyIn: [
    { value: "0", label: "Freeroll" },
    { value: "10", label: "$10" },
    { value: "20", label: "$20" },
    { value: "50", label: "$50" },
    { value: "100", label: "$100" },
    { value: "Custom", label: "Custom" },
  ],
  Fees: [
    { value: "0", label: "None" },
    { value: "5", label: "$5" },
    { value: "10", label: "$10" },
    { value: "20", label: "$20" },
    { value: "Custom", label: "Custom" },
  ],
  Games: [
    { value: "NLH", label: "NLH" },
    { value: "PLO", label: "PLO" },
    { value: "ROE", label: "ROE" },
    { value: "Omaha 8", label: "O8" },
    { value: "Big-O", label: "Big-O" },
    { value: "Mixed", label: "Mixed" },
    { value: "Custom", label: "Custom" },
  ],
  Days: [
    { value: "0", label: "Sunday" },
    { value: "1", label: "Monday" },
    { value: "2", label: "Tuesday" },
    { value: "3", label: "Wednesday" },
    { value: "4", label: "Thursday" },
    { value: "5", label: "Friday" },
    { value: "6", label: "Saturday" },
  ],
} as const;

interface CreateGameProps {
  setDisplay?: (display: string) => void;
}

const CreateGame: React.FC<CreateGameProps> = (props) => {
  const navigate = useNavigate();

  const { gameId } = useParams<{ gameId?: string }>();
  const user = useSelector((state: any) => state.user.user);

  const [isLoading, setIsLoading] = useState(false);
  const [values, setValues] = useState<GameFormData>({
    gameName: "",
    type: "Cash",
    stakes: "",
    game: "",
    fee: "",
    state: "",
    city: "",
    zipcode: "",
    address: "",
    access: "Public",
    accesscode: "",
    minPlayers: 2,
    maxPlayers: 10,
    minbuyin: "",
    maxbuyin: "",
    startMoment: moment(),
    startTime: moment().format("hh:mm A"),
    startDay: 0,
    outputMsg: "",
    customstakes: "",
    customgame: "",
    customfee: "",
    custombuyin: "",
    visible: true,
  });

  const [description, setDescription] = useState("");
  const [errors, setErrors] = useState<Errors>({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (user && gameId) {
      getGameInfo();
    }
  }, [user, gameId]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setValues({ ...values, [name]: value });
  };

  const handleAddressSelect = (addressData: {
    formattedAddress: string;
    latitude: number;
    longitude: number;
    city?: string;
    state?: string;
    postalCode?: string;
  }) => {
    setValues({
      ...values,
      lat: addressData.latitude,
      lng: addressData.longitude,
      // Auto-populate city/state if not already filled and available
      ...(addressData.city && !values.city && { city: addressData.city }),
      ...(addressData.state && !values.state && { state: addressData.state }),
      ...(addressData.postalCode &&
        !values.zipcode && { zipcode: addressData.postalCode }),
    });
  };

  const handleTimeChange = (newTime: moment.Moment | null) => {
    setValues({
      ...values,
      startMoment: newTime || moment(),
      startTime: newTime ? newTime.format("hh:mm A") : "",
    });
  };

  const getGameInfo = async () => {
    if (gameId) {
      try {
        const gameRef = doc(db, "games", gameId);
        const docSnap = await getDoc(gameRef);

        if (docSnap.exists()) {
          const gameData = docSnap.data() as any;
          if (!STAKE_VALUES.includes(gameData.stakes)) {
            gameData.customstakes = gameData.stakes;
            gameData.stakes = "Custom";
          }
          if (!GAME_VALUES.includes(gameData.game)) {
            gameData.customgame = gameData.game;
            gameData.game = "Custom";
          }
          if (!FEE_VALUES.includes(gameData.fee)) {
            gameData.customfee = gameData.fee;
            gameData.fee = "Custom";
          }
          if (!BUYIN_VALUES.includes(gameData.buyin)) {
            gameData.custombuyin = gameData.buyin;
            gameData.buyin = "Custom";
          }
          setValues({
            ...values,
            id: gameData.id,
            gameName: gameData.gameName,
            type: gameData.type,
            game: gameData.game,
            stakes: gameData.stakes,
            fee: gameData.fee,
            city: gameData.city,
            state: gameData.state,
            status: gameData.status,
            zipcode: gameData.zipcode,
            address: gameData.address,
            access: gameData.access,
            accesscode: gameData.accesscode ? gameData.accesscode.split('-').slice(1).join('-') : '',
            buyin: gameData.buyin,
            minbuyin: gameData.minbuyin,
            // Convert "No Limit" back to empty string for editing
            maxbuyin: gameData.maxbuyin === "No Limit" ? "" : gameData.maxbuyin,
            minPlayers: gameData.minPlayers,
            maxPlayers: gameData.maxPlayers,
            startMoment: gameData.startMoment
              ? moment(gameData.startMoment)
              : moment(),
            startTime: gameData.startTime || moment().format("hh:mm A"),
            startDay: gameData.startDay || 0,
            customstakes: gameData.customstakes,
            customgame: gameData.customgame,
            customfee: gameData.customfee,
            custombuyin: gameData.custombuyin,
            visible: gameData.visible !== undefined ? gameData.visible : true,
            outputMsg: "",
          });
          setDescription(gameData.description);
        }
      } catch (err) {
        console.log(`Encountered error: ${err}`);
      }
    }
  };

  const setState = (evt: React.ChangeEvent<HTMLInputElement>) => {
    setValues({
      ...values,
      state: evt.target.value,
      city: "",
    });
  };

  const handleDeleteGame = async () => {
    try {
      if (gameId) {
        await deleteGame(gameId);
        toast.success("Game Deleted Successfully", {
          position: "bottom-center",
        });
        navigate(`/hostpanel`);
      }
    } catch (err) {
      console.error("Error deleting game:", err);
      toast.error("Error Deleting Game", {
        position: "bottom-center",
      });
    }
    setShowDeleteConfirm(false);
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
  };

  // Helper function to remove undefined values and hostId for updates
  const removeUndefinedFields = (obj: any, isUpdate: boolean = false): any => {
    const cleaned: any = {};
    Object.keys(obj).forEach((key) => {
      // Skip hostId for updates since it shouldn't be changed
      if (isUpdate && key === "hostId") {
        return;
      }
      if (obj[key] !== undefined && obj[key] !== null) {
        cleaned[key] = obj[key];
      }
    });
    return cleaned;
  };

  const publishGame = async () => {
    const errs = validateInput(values);
    setErrors(errs);
    console.log(`errors`, errs);

    if (Object.keys(errs).length) {
      setValues({ ...values, outputMsg: "Check Input Fields for Errors" });
    } else {
      setIsLoading(true);
      setValues({ ...values, outputMsg: "" });

      let stakes;
      let game;
      if (values.stakes === "Custom") {
        stakes = values.customstakes;
      } else {
        stakes = values.stakes;
      }
      if (values.game === "Custom") {
        game = values.customgame;
      } else {
        game = values.game;
      }

      const gameData = {
        hostId: user.uid,
        access: values.access,
        gameName: values.gameName,
        description,
        type: values.type,
        game,
        minPlayers: values.minPlayers,
        maxPlayers: values.maxPlayers,
        status: "Interest",
        state: values.state,
        city: values.city,
        zipcode: values.zipcode,
        address: values.address,
        lat: values.lat,
        lng: values.lng,
        startDay: values.startDay,
        startTime: values.startTime,
        timeStamp: Date.now(),
        visible: values.visible,
      };

      let id;
      if (gameId) {
        // Use existing game ID when editing
        id = values.id || gameId;
      } else {
        // Generate new ID when creating
        // Both private and public games now use the same ID generation method
        id = createHash(user.uid.slice(0, 10) + values.gameName + Date.now());
      }

      gameData.id = id;

      if (values.access === "Private") {
        gameData.accesscode = `${user.uid.slice(0, 6)}-${values.accesscode}`;
        gameData.allowedPlayers = {};
      }
      if (values.type === "Cash") {
        // set cashgame specific values
        gameData.stakes = stakes;
        gameData.minbuyin = values.minbuyin;
        // If max buy-in is empty, interpret as "No Limit"
        gameData.maxbuyin = values.maxbuyin || "No Limit";
      } else if (values.type === "Tournament") {
        // set tournament specific values
        if (values.buyin === "Custom") {
          gameData.buyin = values.custombuyin;
        } else {
          gameData.buyin = values.buyin;
        }
        if (values.fee === "Custom") {
          gameData.fee = values.customfee;
        } else {
          gameData.fee = values.fee;
        }
      }

      try {
        if (gameId) {
          const cleanGameData = removeUndefinedFields(gameData, true);
          const res = await editGame({
            gameData: cleanGameData,
            finalCB: () => {
              setIsLoading(false);
            },
          });
          if (res.status === "SUCCESS") {
            toast.success(res.message, {
              position: "bottom-center",
            });
            navigate("../hostpanel");
          } else if (res.status === "ERROR") {
            toast.error(res.message, {
              position: "bottom-center",
            });
            setValues({ ...values, outputMsg: res.message });
            setIsLoading(false);
          }
        } else {
          const cleanGameData = removeUndefinedFields(gameData);
          const res = await createGame({
            gameData: cleanGameData,
            finalCB: () => {
              setIsLoading(false);
              if (props.setDisplay) props.setDisplay("manage");
            },
          });
          if (res.status === "SUCCESS") {
            toast.success(res.message, {
              position: "bottom-center",
            });
            navigate("../hostpanel");
          } else if (res.status === "ERROR") {
            toast.error(res.message, {
              position: "bottom-center",
            });
            setValues({ ...values, outputMsg: res.message });
            setIsLoading(false);
          }
        }
      } catch (error) {
        console.error("Error publishing game:", error);
        const errorMessage = "An error occurred while saving the game";
        toast.error(errorMessage, {
          position: "bottom-center",
        });
        setValues({
          ...values,
          outputMsg: errorMessage,
        });
        setIsLoading(false);
      }
    }
  };

  const renderNameAndDescription = () => (
    <Box mb={4}>
      <Typography variant="h5" gutterBottom color="primary" sx={{ mb: 3 }}>
        Basic Information
      </Typography>
      <Grid2 container spacing={3}>
        <Grid2 size={12}>
          <TextInput
            name="gameName"
            label="Game Name *"
            value={values.gameName}
            onChange={handleInputChange}
            error={errors.gameName}
          />
        </Grid2>
        <Grid2 size={12}>
          <Description
            description={description}
            handleChange={setDescription}
            placeholder="Game Description"
          />
        </Grid2>
      </Grid2>
    </Box>
  );

  const renderTypeOptions = () => (
    <Box mb={4} >
      <Typography variant="h5" gutterBottom color="primary" sx={{ mb: 3 }}>
        Game Type & Access
      </Typography>
      <Grid2 container spacing={3}>
        <Grid2 size={{ xs: 12, sm: 6 }}>
          <SelectField
            name="access"
            value={values.access}
            label="Access"
            onChange={handleInputChange}
            error={errors.access}
            options={OPTIONS.Access}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6 }}>
          <SelectField
            name="type"
            value={values.type}
            label="Type"
            onChange={handleInputChange}
            error={errors.type}
            options={OPTIONS.Type}
          />
        </Grid2>
        <Grid2 size={12}>
          <Tooltip title="When enabled, your game will appear in the public game list for players to find and join. When disabled, your game will be hidden from public view.">
            <FormControlLabel
              control={
                <Switch
                  checked={values.visible}
                  onChange={(e) =>
                    setValues({ ...values, visible: e.target.checked })
                  }
                  name="visible"
                  color="primary"
                />
              }
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    Game Visibility:
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: values.visible ? "success.main" : "text.secondary",
                      fontWeight: 500,
                    }}
                  >
                    {values.visible ? "Visible" : "Hidden"}
                  </Typography>
                </Box>
              }
            />
          </Tooltip>
        </Grid2>
      </Grid2>
    </Box>
  );

  const renderPrivateOptions = () =>
    values.access === "Private" ? (
      <Box mb={4}>
        <Typography variant="h5" gutterBottom color="primary" sx={{ mb: 3 }}>
          Private Game Settings
        </Typography>
        <Grid2 container spacing={3}>
          <Grid2 size={{ xs: 12, md: 6 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <TextField
                required
                fullWidth
                variant="outlined"
                type="text"
                name="accesscode"
                value={values.accesscode}
                onChange={handleInputChange}
                label="Access Code"
                placeholder="Enter 5-10 characters"
                helperText={errors.accesscode}
                error={!!errors.accesscode}
                sx={{ bgcolor: "background.paper", flex: 1 }}
              />
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ 
                  minWidth: "fit-content",
                  fontFamily: "monospace",
                  fontSize: "0.875rem"
                }}
              >
                Full code: {user?.uid?.slice(0, 6)}-{values.accesscode || "xxxxx"}
              </Typography>
            </Box>
          </Grid2>
          <Grid2 size={12}>
            <Box
              sx={{
                p: 2,
                bgcolor: "warning.light",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "warning.main",
              }}
            >
              <Typography variant="body2" color="warning.dark">
                ⚠️ The access code will be needed for players to find your game
              </Typography>
            </Box>
          </Grid2>
        </Grid2>
      </Box>
    ) : null;
  const renderCashOptions = () => (
    <Grid2 container direction="column" spacing={3} size={{ xs: 12, md: 6 }}>
      <Grid2>
        <SelectField
          name="stakes"
          value={values.stakes}
          label="Stakes"
          onChange={handleInputChange}
          error={errors.stakes}
          options={OPTIONS.Stakes}
        />
      </Grid2>
      {values.stakes === "Custom" && (
        <Grid2>
          <TextField
            fullWidth
            variant="outlined"
            type="text"
            name="customstakes"
            required
            value={values.customstakes}
            onChange={handleInputChange}
            label="Custom Stakes"
            sx={{ bgcolor: "background.paper" }}
          />
        </Grid2>
      )}
    </Grid2>
  );

  const renderTournamentOptions = () => (
    <>
      <Grid2 container direction="column" spacing={3} size={{ xs: 12, md: 6 }}>
        <Grid2>
          <SelectField
            name="buyin"
            value={values.buyin || ""}
            label="Buy-In"
            onChange={handleInputChange}
            error={errors.buyin}
            options={OPTIONS.BuyIn}
          />
        </Grid2>
        {values.buyin === "Custom" && (
          <Grid2>
            <TextField
              fullWidth
              variant="outlined"
              type="text"
              name="custombuyin"
              required
              value={values.custombuyin}
              onChange={handleInputChange}
              label="Custom Buy-In"
              sx={{ bgcolor: "background.paper" }}
            />
          </Grid2>
        )}
      </Grid2>
      <Grid2 container direction="column" spacing={3} size={{ xs: 12, md: 6 }}>
        <Grid2>
          <SelectField
            name="fee"
            value={values.fee}
            label="Fee"
            onChange={handleInputChange}
            error={errors.fee}
            options={OPTIONS.Fees}
          />
        </Grid2>
        {values.fee === "Custom" && (
          <Grid2>
            <TextInput
              name="customfee"
              value={values.customfee}
              onChange={handleInputChange}
              label="Custom Fee *"
              error={errors.customfee}
            />
          </Grid2>
        )}
      </Grid2>
    </>
  );

  const renderGameSelect = () => (
    <Grid2 container direction="column" spacing={3} size={{ xs: 12, md: 6 }}>
      <Grid2>
        <SelectField
          name="game"
          value={values.game}
          label="Game"
          onChange={handleInputChange}
          error={errors.game}
          options={OPTIONS.Games}
        />
      </Grid2>
      {values.game === "Custom" && (
        <Grid2>
          <TextInput
            name="customgame"
            value={values.customgame}
            onChange={handleInputChange}
            label="Custom Game *"
            error={errors.customgame}
          />
        </Grid2>
      )}
    </Grid2>
  );

  const renderCashGameBuyInOptions = () => (
    <Box mb={4}>
      <Typography variant="h5" gutterBottom color="primary" sx={{ mb: 3 }}>
        Buy-In
      </Typography>
      <Grid2 container spacing={3}>
        <Grid2 size={{ xs: 12, sm: 6 }}>
          <NumberField
            name="minbuyin"
            value={values.minbuyin}
            label="Min Buy-In"
            onChange={handleInputChange}
            error={errors.minbuyin}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6 }}>
          <NumberField
            name="maxbuyin"
            value={values.maxbuyin}
            label="Max Buy-In"
            onChange={handleInputChange}
            error={errors.maxbuyin}
            placeholder="Leave empty for No Limit"
          />
        </Grid2>
      </Grid2>
    </Box>
  );

  const renderMinMaxPlayers = () => (
    <Box mb={4}>
      <Typography variant="h5" gutterBottom color="primary" sx={{ mb: 3 }}>
        Number of Players
      </Typography>
      <Grid2 container spacing={3}>
        <Grid2 size={{ xs: 12, sm: 6 }}>
          <NumberField
            name="minPlayers"
            value={values.minPlayers}
            label="Min Players *"
            onChange={handleInputChange}
            error={errors.minPlayers}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6 }}>
          <NumberField
            name="maxPlayers"
            value={values.maxPlayers}
            label="Max Players"
            onChange={handleInputChange}
            error={errors.maxPlayers}
          />
        </Grid2>
      </Grid2>
    </Box>
  );

  const renderTimeSelect = () => (
    <Box mb={4}>
      <Typography variant="h5" gutterBottom color="primary" sx={{ mb: 3 }}>
        Date & Time
      </Typography>
      <Grid2 container spacing={3}>
        <Grid2 size={{ xs: 12, sm: 6 }}>
          <SelectField
            name="startDay"
            value={values.startDay}
            label="Start Day"
            onChange={handleInputChange}
            error={errors.startDay}
            options={OPTIONS.Days}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 6 }}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <TimePicker
              label="Start Time"
              value={values.startMoment}
              onChange={handleTimeChange}
              slotProps={{
                textField: {
                  variant: "outlined",
                  fullWidth: true,
                  sx: { bgcolor: "background.paper" },
                },
              }}
            />
          </LocalizationProvider>
        </Grid2>
      </Grid2>
    </Box>
  );

  const renderLocation = () => (
    <Box mb={4}>
      <Typography variant="h5" gutterBottom color="primary" sx={{ mb: 3 }}>
        Location
      </Typography>
      <Box
        sx={{
          mb: 3,
          p: 2,
          bgcolor: "info.light",
          border: "1px solid",
          borderColor: "info.main",
        }}
      >
        <Typography
          variant="body2"
          color="info.dark"
          sx={{ display: "flex", alignItems: "center", gap: 1 }}
        >
          <span>ℹ️</span>
          <span>
            <strong>Privacy Note:</strong> Your location information is used to
            help players find games near them. Your exact address will not be
            publicly displayed to other users. Only general location (city/area)
            will be shown to help with game discovery. If you want to share
            specific location details or meeting instructions with players, you
            can add them to the game description above.
          </span>
        </Typography>
      </Box>
      <Grid2 container spacing={3}>
        <Grid2 size={12}>
          <AutocompleteAddress
            name="address"
            value={values.address}
            label="Address"
            placeholder="Start typing an address..."
            onChange={handleInputChange}
            onAddressSelect={handleAddressSelect}
            error={errors.address}
            required
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 4 }}>
          <CitySelector
            value={values.city}
            searchText={(values as any).citySearch}
            state={values.state}
            onChange={handleInputChange}
            error={errors.city}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 4 }}>
          <StateSelector
            value={values.state}
            onChange={setState}
            error={errors.state}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 4 }}>
          <ZipField
            value={values.zipcode}
            onChange={handleInputChange}
            label="Zip Code"
            error={errors.zipcode}
          />
        </Grid2>
      </Grid2>
    </Box>
  );

  const renderDeleteConfirmDialog = () => (
    <DeleteGameModal
      show={showDeleteConfirm}
      onClose={handleDeleteCancel}
      onConfirm={handleDeleteGame}
      gameName={values.gameName}
    />
  );

  const renderButtons = () => (
    <>
      <Button
        variant="contained"
        color="success"
        onClick={() => publishGame()}
        sx={{ mr: 2, mb: 2 }}
        startIcon={<SaveIcon />}
      >
        {gameId ? "Save Changes" : "Create Game"}
      </Button>
      <br />
      {gameId ? (
        <Button
          variant="contained"
          color="error"
          onClick={handleDeleteClick}
          startIcon={<DeleteForeverIcon />}
        >
          Delete Game
        </Button>
      ) : (
        <></>
      )}
    </>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4, px: { xs: 2, sm: 3, md: 4 } }}>
      <Box display="flex" flexDirection="column" alignItems="center" mb={4}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          {gameId ? "Edit Game" : "Create Game"}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          * Indicates Required Field
        </Typography>
      </Box>

      <form
        onSubmit={(event) => {
          event.preventDefault();
          publishGame();
        }}
      >
        <Grid2 container spacing={0} direction="column">
          {renderNameAndDescription()}
          {renderTypeOptions()}
          {renderPrivateOptions()}

          <Box mb={4}>
            <Typography
              variant="h5"
              gutterBottom
              color="primary"
              sx={{ mb: 3 }}
            >
              Game Configuration
            </Typography>
            <Grid2 container spacing={3}>
              {values.type === "Cash"
                ? renderCashOptions()
                : renderTournamentOptions()}
              {renderGameSelect()}
            </Grid2>
          </Box>

          {values.type === "Cash" && renderCashGameBuyInOptions()}
          {renderMinMaxPlayers()}
          {renderTimeSelect()}
          {renderLocation()}

          <Box
            sx={{
              textAlign: "center",
              mt: 4,
              pt: 3,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            {isLoading ? (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                py={2}
              >
                <CircularProgress />
              </Box>
            ) : (
              renderButtons()
            )}
            {values.outputMsg && (
              <Typography variant="body2" color="error" mt={2}>
                {values.outputMsg}
              </Typography>
            )}
          </Box>
        </Grid2>
      </form>
      {renderDeleteConfirmDialog()}
    </Container>
  );
};

export default CreateGame;
